import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import DaumPostcode from 'react-daum-postcode';
import {
  IoChevronBack,
  IoEyeOutline,
  IoEyeOffOutline,
  IoCheckmarkCircleOutline,
  IoCloseOutline,
  IoDocumentTextOutline,
} from 'react-icons/io5';
import EmailDomainInput from '../../components/common/EmailDomainInput';
import {
  FaTruck,
  FaBroom,
  FaWifi,
  FaPaintRoller,
  FaSnowflake,
  FaGlassWater,
  FaDesktop,
} from 'react-icons/fa6';
import { signup } from '../../services/authService';
import { setConsentPath } from '../../services/consentService';
import { PARTNER_SIGNUP_CATEGORIES } from '../../config/ieumConfig';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { storage, db } from '../../firebase/config';
import { ocrAndExtractBusinessNumber, verifyBusinessLicenseRemote } from '../../services/businessLicenseOcr';
import AlertModal from '../../components/common/AlertModal';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import ieumLogo from '../../assets/ieum-logo.png';
import { usePhoneOtp, formatOtpTime, OTP_LENGTH } from '../../hooks/usePhoneOtp';

const CATEGORY_ICONS = {
  FaTruck,
  FaBroom,
  FaWifi,
  FaPaintRoller,
  FaSnowflake,
  FaGlassWater,
  FaDesktop,
};

// ───────── 약관 항목 ─────────
const TERM_ITEMS = [
  { key: 'age_consent', label: '만 14세 이상이며 모두 동의합니다', required: true, noLink: true },
  { key: 'service', label: '서비스 이용약관', required: true },
  { key: 'privacy', label: '개인정보 처리방침', required: true },
  { key: 'partner_contract', label: '제휴업체 이용약관 및 계약 동의', required: true },
  { key: 'purchase_refund', label: '구매 및 환불 동의', required: true },
  { key: 'operation_policy', label: '운영정책', required: true },
  { key: 'marketing', label: '광고성 정보 수신 등 수신에 모두 동의합니다', required: false },
];

// ───────── 유틸 ─────────
const formatPhone = (val) => {
  const d = val.replace(/[^0-9]/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const formatBizPhone = (val) => {
  const d = val.replace(/[^0-9]/g, '').slice(0, 11);
  if (d.length < 3) return d;
  if (d.length < 7) return `${d.slice(0, 2)}-${d.slice(2)}`;
  if (d.length < 11) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const formatBizNo = (val) => {
  const d = val.replace(/[^0-9]/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validatePw = (v) => v.length >= 8 && /[A-Za-z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v);

// ───────── 컴포넌트 ─────────
const PartnerSignupPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const submittingRef = useRef(false);

  // 계정
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  // 사업자
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [repPhone, setRepPhone] = useState('');
  const repPhoneDigits = repPhone.replace(/\D/g, '');
  const otp = usePhoneOtp({ phoneDigits: repPhoneDigits, purpose: 'signup' });
  const otpInputsRef = useRef([]);
  const [managerName, setManagerName] = useState('');
  const [managerPhone, setManagerPhone] = useState('');
  const [bizNo, setBizNo] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [licenseFile, setLicenseFile] = useState(null);

  // 서비스
  const [selectedCategories, setSelectedCategories] = useState([]);

  // 약관
  const [terms, setTerms] = useState(
    TERM_ITEMS.reduce((acc, t) => ({ ...acc, [t.key]: false }), {})
  );

  // UI 상태
  const [addressModal, setAddressModal] = useState(false);
  const [doneModal, setDoneModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertModal, setAlertModal] = useState(null);

  // 비밀번호 검증
  const pwMismatch = passwordConfirm && password !== passwordConfirm;
  const pwMatch = passwordConfirm && password === passwordConfirm;

  // 약관
  const requiredKeys = TERM_ITEMS.filter((t) => t.required).map((t) => t.key);
  const allRequiredChecked = requiredKeys.every((k) => terms[k]);
  const allChecked = TERM_ITEMS.every((t) => terms[t.key]);

  const toggleAll = () => {
    const next = !allChecked;
    setTerms(TERM_ITEMS.reduce((acc, t) => ({ ...acc, [t.key]: next }), {}));
  };

  const toggleTerm = (key) => () => {
    setTerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCategory = (id) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB 이하여야 합니다.');
      return;
    }
    setLicenseFile(file);
  };

  const handleAddressSelect = (data) => {
    const picked = data?.roadAddress || data?.jibunAddress || data?.address || '';
    setAddress(picked);
    setAddressModal(false);
  };

  const showError = (msg) => setAlertModal({ type: 'error', title: '입력 확인', message: msg });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 더블클릭/연속 제출 동기 차단 — setLoading은 비동기라 빠른 두 번째 클릭이 통과해 중복 가입이 발생함
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      // 유효성 검사 (실패 시 AlertModal로 안내)
      if (!validateEmail(email)) return showError('이메일 형식이 올바르지 않습니다.');
      if (!validatePw(password)) return showError('비밀번호는 영문/숫자/기호 포함 8자 이상이어야 합니다.');
      if (password !== passwordConfirm) return showError('비밀번호가 일치하지 않습니다.');
      if (!businessName.trim()) return showError('업체명을 입력해 주세요.');
      if (!ownerName.trim()) return showError('대표자명을 입력해 주세요.');
      if (!repPhone) return showError('대표자 휴대폰 번호를 입력해 주세요.');
      if (!otp.verified) return showError('대표자 휴대폰 번호 인증을 완료해 주세요.');
      const bizNoDigits = bizNo.replace(/\D/g, '');
      if (bizNoDigits.length !== 10) return showError('사업자등록번호 10자리를 입력해 주세요.');
      if (!licenseFile) return showError('사업자등록증을 업로드해 주세요.');
      if (selectedCategories.length === 0) return showError('제공 서비스를 1개 이상 선택해 주세요.');
      if (!allRequiredChecked) return showError('필수 약관에 모두 동의해 주세요.');

      // 클릭 즉시 로딩 오버레이 노출 (OCR 3-5초 + 가입 처리 중)
      setLoading(true);

      // OCR로 사업자번호 일치 확인
      // - BIZNO_MISMATCH: 위변조 의심 → 가입 차단
      // - NO_BIZNO_FOUND: 사업자등록증 아닌 파일로 보임 → 가입은 진행, 백엔드에서 자동반려 처리
      let ocrResult;
      let ocrFailed = false;
      try {
        ocrResult = await ocrAndExtractBusinessNumber(licenseFile, bizNoDigits);
      } catch (ocrErr) {
        console.error('사업자등록증 OCR 실패:', ocrErr);
        ocrFailed = true;
        ocrResult = { ok: false, reason: 'NO_BIZNO_FOUND' };
      }
      if (!ocrResult.ok) {
        if (ocrResult.reason === 'BIZNO_MISMATCH') {
          return showError('입력하신 사업자번호와 사업자등록증의 사업자번호가 일치하지 않습니다.');
        }
        ocrFailed = true;
      }
      // 국세청 사전 검증은 가입 차단 안 함 — 만료/폐업이어도 가입 진행, 로그인 시점에 차단

      // 선택 필드 → 대표값으로 치환
      const finalManagerName = managerName.trim() || ownerName.trim();
      const finalManagerPhone = managerPhone || repPhone;

      const { uid } = await signup({
        email: email.trim(),
        password,
        name: ownerName.trim(),
        phone: repPhone,
        businessName: businessName.trim(),
        role: 'partner',
        verifiedToken: otp.verifiedToken,
        agreedTermKeys: TERM_ITEMS.filter((t) => terms[t.key]).map((t) => t.key),
        profile: {
          business_name: businessName.trim(),
          // 대표자 (필수)
          representative_name: ownerName.trim(),
          representative_phone: repPhone,
          owner_name: ownerName.trim(), // 하위호환
          // 담당자 (선택 — 미입력 시 대표자값 복사)
          manager_name: finalManagerName,
          manager_phone: finalManagerPhone,
          // 선택 필드
          business_number: bizNo || '',
          business_address: address.trim(),
          business_address_detail: addressDetail.trim(),
          address: address.trim(), // 하위호환
          address_detail: addressDetail.trim(), // 하위호환
          office_phone: bizPhone || '',
          business_phone: bizPhone || '', // 하위호환
          service_categories: selectedCategories,
          license_file_name: licenseFile?.name || '',
          terms_agreed: terms,
        },
      });

      // 사업자등록증 Storage 업로드 + 국세청 자동검증 호출
      if (licenseFile && uid) {
        try {
          const ext = licenseFile.name.split('.').pop();
          const fileRef = storageRef(storage, `partners/${uid}/license.${ext}`);
          await uploadBytes(fileRef, licenseFile);
          const url = await getDownloadURL(fileRef);
          await updateDoc(doc(db, 'partners', uid), {
            license_url: url,
            business_license_url: url,
            auto_verification_status: 'ocr_processing',
            license_uploaded_at: new Date(),
          });
          // 국세청 상태조회는 백그라운드 — 결과 안 기다림 (UX 끊김 방지)
          verifyBusinessLicenseRemote({
            partnerId: uid,
            businessNumber: bizNoDigits,
            licenseUrl: url,
            ocrFields: ocrResult?.fields || null,
            ocrFailed,
          }).catch((verifyErr) => console.error('자동검증 호출 실패:', verifyErr));
        } catch (uploadErr) {
          console.error('사업자등록증 업로드 실패:', uploadErr);
        }
      }

      setConsentPath('landing_signup_partner');
      setDoneModal(true);
    } catch (err) {
      const code = err?.code || '';
      // 대표자/담당자 연락처 중복 → 로그인 페이지로 튕기기
      const isPhoneDup =
        code === 'auth/phone-already-in-use' ||
        code === 'auth/manager-phone-already-in-use' ||
        code === 'auth/phone-already-linked';
      const isBizDup = code === 'partner/business-number-already-in-use';
      if (isPhoneDup || isBizDup) {
        setAlertModal({
          type: 'error',
          title: isBizDup ? '이미 가입된 사업자등록번호입니다.' : '이미 가입된 연락처입니다.',
          message: isBizDup
            ? '동일한 사업자등록번호로 가입된 계정이 있습니다.\n로그인 후 이용해 주세요.'
            : '이미 등록된 대표자 또는 담당자 연락처입니다.\n로그인 후 이용해 주세요.',
          onConfirm: () => navigate('/login', { replace: true }),
        });
        return;
      }
      let msg;
      if (code === 'auth/withdrawn') msg = '탈퇴된 계정입니다. 동일한 이메일/전화번호로는 재가입이 불가합니다.';
      else if (code === 'auth/email-already-in-use') msg = '이미 가입된 이메일입니다.';
      else if (code === 'auth/weak-password') msg = '비밀번호는 6자 이상이어야 합니다.';
      else if (code === 'auth/invalid-email') msg = '이메일 형식이 올바르지 않습니다.';
      else msg = err?.message || '가입 신청에 실패했습니다. 잠시 후 다시 시도해 주세요.';
      setAlertModal({ type: 'error', title: '가입 실패', message: msg });
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <Page>
      {(loading || otp.loading) && (
        <LoadingOverlay
          message={
            loading
              ? '가입 신청을 처리 중입니다.\n잠시만 기다려 주세요...'
              : '인증번호를 발송 중입니다...'
          }
        />
      )}
      <Header>
        <HeaderInner>
          <BackBtn
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/login', { replace: true });
            }}
            aria-label="뒤로가기"
          >
            <IoChevronBack size={22} />
          </BackBtn>
          <HeaderTitle>회원가입</HeaderTitle>
        </HeaderInner>
      </Header>

      <Content>
        {/* 브랜드 */}
        <BrandRow>
          <LogoImg src={ieumLogo} alt="이음" />
        </BrandRow>
        <HeroTitle>
          이음 <HeroBlue>파트너</HeroBlue>에<br />
          오신 것을 환영합니다
        </HeroTitle>
        <HeroSub>간편하게 가입하고 서비스를 시작해 보세요.</HeroSub>

        {/* 카카오 버튼 (UI만) */}
        <KakaoBtn type="button" disabled>
          <KakaoIcon viewBox="0 0 24 24" width="20" height="20">
            <path d="M12 3C6.48 3 2 6.36 2 10.44c0 2.61 1.74 4.9 4.36 6.2-.14.52-.9 3.34-.93 3.55 0 0-.02.16.08.22.1.06.22.03.22.03.3-.04 3.44-2.26 3.98-2.64.74.1 1.52.16 2.3.16 5.52 0 10-3.36 10-7.52S17.52 3 12 3z" fill="#3C1E1E"/>
          </KakaoIcon>
          카카오로 3초만에 가입하기
        </KakaoBtn>

        <Divider>직접 가입하기</Divider>

        <form onSubmit={handleSubmit}>
          {/* ─── ① 계정 정보 ─── */}
          <Section>
            <SectionTitle>
              <SectionBadge>1</SectionBadge>
              계정 정보
            </SectionTitle>

            <Field>
              <Label>이메일 <LabelHint>(로그인 아이디로 사용됩니다)</LabelHint></Label>
              <EmailDomainInput
                value={email}
                onChange={setEmail}
                placeholder="example"
                inputClassName="rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[15px] text-slate-900 outline-none transition focus:border-blue-400"
              />
            </Field>

            <Field>
              <Label>비밀번호</Label>
              <InputWrap>
                <Input
                  type={showPw ? 'text' : 'password'}
                  placeholder="영문자, 숫자, 기호 포함 8자 이상"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  $hasIcon
                />
                <EyeButton type="button" onClick={() => setShowPw((v) => !v)}>
                  {showPw ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                </EyeButton>
              </InputWrap>
            </Field>

            <Field>
              <Label>비밀번호 확인</Label>
              <InputWrap>
                <Input
                  type={showPw2 ? 'text' : 'password'}
                  placeholder="영문자, 숫자, 기호 포함 8자 이상"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  $hasIcon
                  $error={pwMismatch}
                />
                <EyeButton type="button" onClick={() => setShowPw2((v) => !v)}>
                  {showPw2 ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                </EyeButton>
              </InputWrap>
              {pwMatch && <ValidMsg>입력한 비밀번호가 일치합니다.</ValidMsg>}
              {pwMismatch && <ErrorInline>입력한 비밀번호가 일치하지 않습니다.</ErrorInline>}
            </Field>
          </Section>

          {/* ─── ② 사업자 정보 ─── */}
          <Section>
            <SectionTitle>
              <SectionBadge>2</SectionBadge>
              사업자 정보
            </SectionTitle>

            <Field>
              <Label>업체명 <RequiredMark>*</RequiredMark></Label>
              <Input
                type="text"
                placeholder="정식 사업자명"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </Field>

            <Field>
              <Label>사업자등록증 <RequiredMark>*</RequiredMark></Label>
              <FileUploadWrap>
                <FilePreview $hasFile={!!licenseFile}>
                  {licenseFile ? (
                    <>
                      <IoDocumentTextOutline size={18} />
                      <FileName>{licenseFile.name}</FileName>
                      <FileRemoveBtn type="button" onClick={() => setLicenseFile(null)}>
                        <IoCloseOutline size={16} />
                      </FileRemoveBtn>
                    </>
                  ) : (
                    <FilePlaceholder>PDF, JPG, PNG 10MB 이하</FilePlaceholder>
                  )}
                </FilePreview>
                <FileSelectBtn type="button" onClick={() => fileInputRef.current?.click()}>
                  파일 선택
                </FileSelectBtn>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />
              </FileUploadWrap>
            </Field>

            <Field>
              <Label>대표자명 <RequiredMark>*</RequiredMark></Label>
              <Input
                type="text"
                placeholder="홍길동"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
            </Field>

            <Field>
              <Label>대표자 휴대폰 <RequiredMark>*</RequiredMark></Label>
              <OtpRow>
                <Input
                  type="tel"
                  placeholder="010-1234-5678"
                  value={repPhone}
                  onChange={(e) => {
                    if (otp.verified) return;
                    setRepPhone(formatPhone(e.target.value));
                  }}
                  readOnly={otp.verified}
                  style={otp.verified ? { background: '#F5F7FA', color: '#667085' } : undefined}
                />
                <OtpSendBtn
                  type="button"
                  onClick={otp.sendOtp}
                  disabled={otp.loading || repPhoneDigits.length < 10 || otp.verified}
                >
                  {otp.verified ? '인증완료' : otp.sent ? '재전송' : '인증번호 받기'}
                </OtpSendBtn>
              </OtpRow>

              {otp.sent && !otp.verified && (
                <OtpPanel>
                  <OtpPanelHead>
                    <span>인증번호 6자리</span>
                    <OtpTimer $expired={otp.remainSec <= 0}>{formatOtpTime(otp.remainSec)}</OtpTimer>
                  </OtpPanelHead>
                  <OtpCodeGrid>
                    {otp.code.map((v, i) => (
                      <OtpCodeInput
                        key={i}
                        ref={(el) => { otpInputsRef.current[i] = el; }}
                        value={v}
                        onChange={(e) => otp.setCodeAt(i, e.target.value, otpInputsRef)}
                        onKeyDown={(e) => otp.handleCodeKey(i, e, otpInputsRef)}
                        onPaste={(e) => otp.handleCodePaste(e, otpInputsRef)}
                        type="text"
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={6}
                        disabled={otp.loading || otp.remainSec <= 0}
                      />
                    ))}
                  </OtpCodeGrid>
                  <OtpVerifyBtn
                    type="button"
                    onClick={otp.verify}
                    disabled={otp.loading || otp.codeString.length !== OTP_LENGTH || otp.remainSec <= 0}
                  >
                    {otp.loading ? '확인 중...' : '인증 확인'}
                  </OtpVerifyBtn>
                </OtpPanel>
              )}

              {otp.verified && <ValidMsg>전화번호 인증이 완료되었습니다.</ValidMsg>}
              {otp.errMsg && !otp.verified && <ErrorInline>{otp.errMsg}</ErrorInline>}
            </Field>

            <FieldRow>
              <Field>
                <Label>담당자명 <LabelHintInline>(선택)</LabelHintInline></Label>
                <Input
                  type="text"
                  placeholder="미입력 시 대표자명 사용"
                  value={managerName}
                  onChange={(e) => setManagerName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>담당자 휴대폰 <LabelHintInline>(선택)</LabelHintInline></Label>
                <Input
                  type="tel"
                  placeholder="미입력 시 대표자 번호 사용"
                  value={managerPhone}
                  onChange={(e) => setManagerPhone(formatPhone(e.target.value))}
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field>
                <Label>사업자등록번호 <RequiredMark>*</RequiredMark></Label>
                <Input
                  type="text"
                  placeholder="000-00-00000"
                  value={bizNo}
                  onChange={(e) => setBizNo(formatBizNo(e.target.value))}
                />
              </Field>
              <Field>
                <Label>대표 전화 <LabelHintInline>(선택)</LabelHintInline></Label>
                <Input
                  type="tel"
                  placeholder="02-123-4567"
                  value={bizPhone}
                  onChange={(e) => setBizPhone(formatBizPhone(e.target.value))}
                />
              </Field>
            </FieldRow>

            <Field>
              <Label>사업장 주소 <LabelHintInline>(선택)</LabelHintInline></Label>
              <SearchInputWrap>
                <Input
                  type="text"
                  placeholder="주소 검색"
                  value={address}
                  readOnly
                  onClick={() => setAddressModal(true)}
                  style={{ cursor: 'pointer', paddingRight: '92px' }}
                />
                <SearchBtn type="button" onClick={() => setAddressModal(true)}>
                  주소 검색
                </SearchBtn>
              </SearchInputWrap>
            </Field>

            <Field>
              <Label>상세주소 <LabelHintInline>(선택)</LabelHintInline></Label>
              <Input
                type="text"
                placeholder="동·호수 등 상세주소를 입력해 주세요."
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
              />
            </Field>
          </Section>

          {/* ─── ③ 제공 서비스 ─── */}
          <Section>
            <SectionTitle>
              <SectionBadge>3</SectionBadge>
              제공 서비스
            </SectionTitle>
            <SectionDesc>파트너님이 고객에게 제공 가능하신 모든 업종을 선택해 주세요.</SectionDesc>

            <CategoryGrid>
              {PARTNER_SIGNUP_CATEGORIES.map((cat) => {
                const selected = selectedCategories.includes(cat.id);
                const Icon = CATEGORY_ICONS[cat.icon];
                return (
                  <CategoryBtn
                    key={cat.id}
                    type="button"
                    $selected={selected}
                    onClick={() => toggleCategory(cat.id)}
                  >
                    {Icon && <Icon size={16} />}
                    {cat.label}
                  </CategoryBtn>
                );
              })}
            </CategoryGrid>
          </Section>

          {/* ─── ④ 약관 동의 ─── */}
          <Section>
            <SectionTitle>
              <SectionBadge>4</SectionBadge>
              약관 동의
            </SectionTitle>

            <AllCheckRow onClick={toggleAll}>
              <CustomCheckbox $checked={allChecked} />
              전체 동의
            </AllCheckRow>

            {TERM_ITEMS.map((t) => (
              <TermItemRow key={t.key}>
                <TermItemLabel onClick={toggleTerm(t.key)}>
                  <CustomCheckbox $checked={terms[t.key]} />
                  [{t.required ? '필수' : '선택'}] {t.label}
                </TermItemLabel>
                {t.required && !t.noLink && (
                  <TermViewLink to={`/terms/${t.key}`}>약관보기</TermViewLink>
                )}
              </TermItemRow>
            ))}
          </Section>

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? '처리 중...' : '가입 완료'}
          </SubmitBtn>
        </form>

        <BottomLine>
          이미 계정이 있으신가요?
          <Link to="/partner/login">로그인하기</Link>
        </BottomLine>
      </Content>

      {/* 주소 검색 모달 */}
      {addressModal && (
        <AddressSearchModal
          onClose={() => setAddressModal(false)}
          onSelect={handleAddressSelect}
        />
      )}

      {/* 공통 알림 모달 */}
      {alertModal && (
        <AlertModal
          type={alertModal.type}
          title={alertModal.title}
          message={alertModal.message}
          onConfirm={() => {
            const cb = alertModal.onConfirm;
            setAlertModal(null);
            if (typeof cb === 'function') cb();
          }}
        />
      )}

      {/* 가입 접수 완료 모달 */}
      {doneModal && (
        <ModalOverlay>
          <DoneModal>
            <DoneIcon>
              <IoCheckmarkCircleOutline size={56} />
            </DoneIcon>
            <DoneTitle>이음 파트너스 가입이 신청되셨습니다.</DoneTitle>
            <DoneDesc>
              승인 시 문자로 안내드리겠습니다.
            </DoneDesc>
            <DoneBtn type="button" onClick={() => navigate('/partner/login', { replace: true })}>
              확인
            </DoneBtn>
          </DoneModal>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default PartnerSignupPage;

// ───────── 주소 검색 모달 (카카오/다음 우편번호) ─────────
const AddressSearchModal = ({ onClose, onSelect }) => {
  return (
    <ModalOverlay onClick={onClose}>
      <AddressModalBox onClick={(e) => e.stopPropagation()}>
        <AddressModalHeader>
          <AddressModalTitle>주소 검색</AddressModalTitle>
          <ModalCloseBtn type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </ModalCloseBtn>
        </AddressModalHeader>

        <DaumPostcode
          onComplete={onSelect}
          style={{ width: '100%', height: 440 }}
          autoClose={false}
        />
      </AddressModalBox>
    </ModalOverlay>
  );
};

// ───────── 스타일 ─────────
const Page = styled.div`
  min-height: 100vh;
  overflow-x: clip;
  background: ${({ theme }) => theme.colors.bgWhite};
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    background: #2f3542;
    align-items: center;
    padding: 40px 0;
  }
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.bgWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  z-index: 10;

  @media (min-width: 1024px) {
    position: static;
    width: 100%;
    max-width: 560px;
    background: #fff;
    border: 0;
    border-radius: 16px 16px 0 0;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.md};

  @media (min-width: 1024px) {
    max-width: 560px;
    height: 60px;
    padding: 0 32px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const HeaderTitle = styled.h1`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin: 0 0 0 ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
`;

const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.realtor.pagePadding} 60px;

  @media (min-width: 1024px) {
    flex: none;
    max-width: 560px;
    background: #fff;
    border-radius: 0 0 16px 16px;
    padding: 40px 48px 60px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const LogoImg = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const HeroTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: 1.4;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const HeroBlue = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const HeroSub = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const KakaoBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: #FEE500;
  color: #3C1E1E;
  border: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xxl};

  &:disabled { opacity: 0.85; cursor: not-allowed; }
`;

const KakaoIcon = styled.svg`
  flex-shrink: 0;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
  margin: ${({ theme }) => theme.spacing.xl} 0;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing.xxl};
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.base};
`;

const SectionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const SectionDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const Field = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const FieldRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const LabelHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  margin-left: 4px;
`;

const LabelHintInline = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  margin-left: 4px;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  margin-left: 2px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '48px' : '16px')};
  border: 1px solid ${({ $error, theme }) => ($error ? theme.colors.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  background: ${({ theme }) => theme.colors.bgWhite};

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  padding: 4px;
  cursor: pointer;
`;

const ValidMsg = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.success};

  &::before { content: '✓'; font-weight: bold; }
`;

const ErrorInline = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};

  &::before { content: '✕'; font-weight: bold; }
`;

const OtpRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;

  input {
    flex: 0 1 50%;
    min-width: 0;
  }
`;

const OtpSendBtn = styled.button`
  flex-shrink: 0;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const OtpPanel = styled.div`
  margin-top: 10px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: #FAFBFF;
`;

const OtpPanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const OtpTimer = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ $expired, theme }) => ($expired ? theme.colors.danger : theme.colors.primary)};
`;

const OtpCodeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  margin-bottom: 10px;
`;

const OtpCodeInput = styled.input`
  width: 100%;
  aspect-ratio: 1;
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  text-align: center;
  background: #fff;
  outline: none;
  padding: 0;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
  &:disabled { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const OtpVerifyBtn = styled.button`
  width: 100%;
  padding: 10px;
  background: ${({ theme }) => theme.colors.text};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SearchInputWrap = styled.div`
  position: relative;
`;

const SearchBtn = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px 14px;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
`;

const FileUploadWrap = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: stretch;
`;

const FilePreview = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ $hasFile, theme }) => ($hasFile ? theme.colors.text : theme.colors.textLight)};
  font-size: ${({ theme }) => theme.fontSize.base};
  min-width: 0;
`;

const FileName = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
`;

const FilePlaceholder = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
`;

const FileRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: 50%;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
`;

const FileSelectBtn = styled.button`
  padding: 0 18px;
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  white-space: nowrap;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CategoryBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  min-width: 0;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $selected, theme }) => ($selected ? `${theme.colors.primary}10` : theme.colors.bgWhite)};
  border: 1px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.border)};
  color: ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.text)};
`;

const AllCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const CustomCheckbox = styled.span`
  width: 22px;
  height: 22px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 2px solid ${({ $checked, theme }) => ($checked ? theme.colors.primary : theme.colors.border)};
  background: ${({ $checked, theme }) => ($checked ? theme.colors.primary : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  &::after {
    content: '';
    width: 6px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
    margin-top: -1px;
  }
`;

const TermItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
`;

const TermItemLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const TermViewLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled, theme }) => (disabled ? theme.colors.border : theme.colors.primary)};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.textLight : '#fff')};
  margin-top: ${({ theme }) => theme.spacing.xxl};
`;

const BottomLine = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-bottom: ${({ theme }) => theme.spacing.xl};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    margin-left: 8px;
    text-decoration: none;
  }
`;

// ───────── 모달 공통 ─────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: ${({ theme }) => theme.spacing.lg};
`;

// ───────── 주소 모달 ─────────
const AddressModalBox = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const AddressModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const AddressModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const ModalCloseBtn = styled.button`
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  padding: 0;
`;

// ───────── 완료 모달 ─────────
const DoneModal = styled.div`
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const DoneIcon = styled.div`
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const DoneTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const DoneDesc = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin: 0 0 ${({ theme }) => theme.spacing.xl};

  strong {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
  }
`;

const DoneBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
`;
